import { useEffect, useMemo, useRef, useState } from "react";
import {
  ACTIONS,
  SKIP_REVIEW_EPA_SUMMARY_STORAGE_KEY,
  STEPS,
} from "./config";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import {
  useAddFeedbackMutation,
  useAddFeedbackSkillMutation,
  useAddFeedbackTagsMutation,
  useGetDataRequestFeedbackLazyQuery,
  useGetFeedbackByIdLazyQuery,
  useGetUsersByOrgIdAndRoleLazyQuery,
  useGetUserByIdLazyQuery,
  useUpdateFeedbackMutation,
  useUpdateFeedbackSkillMutation,
  useGetValidFeedbacksByStudentIdLazyQuery,
  useGetLearnersByOrgIdLazyQuery,
  useGetUsersRoleByOrgLazyQuery,
} from "graphql/__generated__/hooks";
import { useGetOrgCustomLabelsByOrgQuery } from "graphql/__generated__/hooks";
import { useAppSelector } from "hooks/useAppSelector";
import { userSelector } from "reduxStore/auth/selectors";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  sendAlertViaEmail,
  showNotificationThunk,
} from "reduxStore/alert/thunks";
import { ErrorType } from "./types";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { Tag } from "types/tag";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useRequestFeedbackData } from "./useRequestFeedbackData";
import { ALERT_TYPES } from "helpers/constants";
import { getFeedbackStatus } from "utils/feedback";
import { getLearnerObservationDateError } from "./observationDate";
import { CoachRoutes } from "config/routes";
import { ROLES, User } from "../../../types/user";
import { REQUIREMENT } from "helpers/constants";
import { useBadgeChecker } from "hooks/useBadgeChecker";
import { VETCOIN_REWARDS, useVetcoin } from "hooks/useVetcoin";

export const useRequestFeedback = () => {
  const { feedbackId, userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isCoachInitiated = location.pathname.startsWith(
    CoachRoutes.GiveFeedback,
  );
  const { checkAndAwardBadge } = useBadgeChecker();
  const [getDataRequestFeedback, { data: dataFeedback }] =
    useGetDataRequestFeedbackLazyQuery();
  const [getFeedbackById, { data: dataFeedbackById }] =
    useGetFeedbackByIdLazyQuery();

  const [addFeedback] = useAddFeedbackMutation();
  const [addFeedbackSkill] = useAddFeedbackSkillMutation();
  const [addFeedbackTags] = useAddFeedbackTagsMutation();

  const [updateFeedbackSkill] = useUpdateFeedbackSkillMutation();
  const [updateFeedback] = useUpdateFeedbackMutation();
  const [getCoachesByOrg, { data: coachesByOrgData }] =
    useGetUsersByOrgIdAndRoleLazyQuery();
  const [getLearnersByOrgId, { data: learnersByOrgData }] =
    useGetLearnersByOrgIdLazyQuery();
  const [getOrgStudents, { data: orgStudentsData }] =
    useGetUsersRoleByOrgLazyQuery();
  const [getUserData, { data: userData }] = useGetUserByIdLazyQuery();
  const [getHistoricalFeedbacks, { data: historicalFeedbacksData }] =
    useGetValidFeedbacksByStudentIdLazyQuery();

  const user = useAppSelector(userSelector);

  useEffect(() => {
    if (isCoachInitiated) {
      if (userId) {
        getUserData({ variables: { userId } }).then();
      }
      return;
    }
    const id = userId || user?.user_id;
    if (id) {
      getUserData({
        variables: {
          userId: id,
        },
      }).then();
    }
  }, [userId, user?.user_id, getUserData, isCoachInitiated]);

  const { data, dispatch, error, setError } = useRequestFeedbackData();

  useEffect(() => {
    if (!isCoachInitiated || !user) return;
    dispatch({
      type: ACTIONS.SET_SELECTED_COACH,
      payload: user,
    });
  }, [isCoachInitiated, user, dispatch]);

  useEffect(() => {
    if (!isCoachInitiated || !userId || !userData?.user_by_pk) return;
    dispatch({
      type: ACTIONS.SET_SELECTED_LEARNER,
      payload: userData.user_by_pk,
    });
  }, [isCoachInitiated, userId, userData, dispatch]);

  const selectedUser = useMemo(() => {
    if (isCoachInitiated) {
      return data.selectedLearner || userData?.user_by_pk || null;
    }
    return userData?.user_by_pk || user;
  }, [isCoachInitiated, data.selectedLearner, userData, user]);

  const { awardVetcoins } = useVetcoin(
    isCoachInitiated ? user?.user_id : selectedUser?.user_id,
  );

  const dispatchRedux = useAppDispatch();

  const feedbackIdRef = useRef<string | null>(null);
  const feedbackSkillIdRef = useRef<string | null>(null);
  // Track feedback_skill_ids for ITER sub-requirements: requirement_id -> feedback_skill_id
  const iterFeedbackSkillIdsRef = useRef<Record<string, string>>({});
  // Prevent concurrent saves
  const isSavingRef = useRef<boolean>(false);

  const [timerStart, setTimerStart] = useState<string | null>(null);
  const [displayTimer, setDisplayTimer] = useState<boolean>(false);

  const sites = useMemo(() => {
    return dataFeedback?.organization_site.map((site) => site.site) || [];
  }, [dataFeedback]);

  const requirements = useMemo(() => {
    return (
      dataFeedback?.graduation_class_student
        .map((gcs) =>
          gcs.graduation_class?.current_academic_year?.requirements.map(
            (req) => req.requirement,
          ),
        )
        .flat() || []
    );
  }, [dataFeedback]);

  const tags = useMemo(() => {
    return dataFeedback?.tag || [];
  }, [dataFeedback]);

  const organization = useAppSelector(organizationSelectedSelector);
  const orgId =
    organization?.org_id ||
    selectedUser?.org_id ||
    user?.org_id ||
    (userData?.user_by_pk as any)?.organization?.[0]?.organization?.org_id ||
    undefined;

  const [isCompleted, setIsCompleted] = useState(false);

  // Load organization custom labels (if available)
  const { data: orgCustomLabelsData } = useGetOrgCustomLabelsByOrgQuery({
    skip: !orgId,
    variables: { orgId: orgId as string },
    fetchPolicy: "no-cache",
  });

  const customLabels = useMemo(() => {
    const list =
      orgCustomLabelsData?.organization_custom_label?.map((ocl: any) =>
        ocl?.custom_label ? ocl.custom_label : null,
      ) || [];
    return (list.filter(Boolean) as any[]) || [];
  }, [orgCustomLabelsData]);

  const feedback = useMemo(() => {
    return dataFeedbackById?.feedback_by_pk as FeedbackType | null;
  }, [dataFeedbackById]);

  const status = useMemo(() => {
    if (!feedback) return null;
    return getFeedbackStatus(feedback as FeedbackType) as FeedbackStatus;
  }, [feedback]);

  useEffect(() => {
    if (status === FeedbackStatus.MISSING_INFO) {
      const isITER =
        data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;
      setStep(isITER ? STEPS.PERFORM : STEPS.REQUEST1);
    }
  }, [status, data.selectedCompetencySkill?.type]);

  useEffect(() => {
    if (feedbackId) {
      getFeedbackById({ variables: { feedbackId } }).then((response) => {
        if (response.data?.feedback_by_pk) {
          dispatch({
            type: ACTIONS.INITIAL_DATA,
            payload: response.data.feedback_by_pk,
          });
          setTimerStart(response.data.feedback_by_pk.created_at);
          setDisplayTimer(true);
        }

        // set the feedback id and feedback skill id to call update api
        feedbackIdRef.current = feedbackId;
        feedbackSkillIdRef.current =
          response.data?.feedback_by_pk?.feedback_skills?.[0]?.feedback_skill_id;

        // Track feedback_skill_ids for ITER sub-requirements
        if (response.data?.feedback_by_pk?.feedback_skills) {
          response.data.feedback_by_pk.feedback_skills.forEach((skill: any) => {
            if (skill.requirement?.requirement_id && skill.feedback_skill_id) {
              iterFeedbackSkillIdsRef.current[
                skill.requirement.requirement_id
              ] = skill.feedback_skill_id;
            }
          });
        }
      });
    }
  }, [feedbackId, getFeedbackById]);

  const [step, setStep] = useState(STEPS.REQUEST2);
  const [activeLabel, setActiveLabel] = useState(0);

  useEffect(() => {
    if (selectedUser?.user_id && orgId) {
      getDataRequestFeedback({
        variables: {
          user_id: selectedUser?.user_id,
          org_id: orgId,
        },
      }).then();
    }
  }, [selectedUser, orgId, getDataRequestFeedback]);

  // Fetch historical feedbacks for ITER ratings charts
  useEffect(() => {
    if (selectedUser?.user_id) {
      getHistoricalFeedbacks({
        variables: { studentId: selectedUser.user_id },
      }).then();
    }
  }, [selectedUser?.user_id, getHistoricalFeedbacks]);

  useEffect(() => {}, [isCompleted]);

  /* ************************** IS VALID ************************** */
  const isValid = () => {
    // Validate that the data is valid for the step
    let newError: ErrorType = { ...error };
    let isValid = false;

    if (step === STEPS.PERFORM) return true;

    if (step === STEPS.REQUEST) {
      newError["name"] = !data.name ? "Please enter a pet name" : null;
      newError["caseDate"] = isCoachInitiated
        ? !data.caseDate
          ? "Please select a case date"
          : null
        : getLearnerObservationDateError(data.caseDate);
      newError["caseTime"] = !data.caseTime
        ? "Please select a case time"
        : null;

      isValid = ![newError.name, newError.caseDate, newError.caseTime].some(
        (val) => val !== null,
      );
    }

    if (step === STEPS.REQUEST1) {
      isValid = true;
    }

    if (step === STEPS.REQUEST2) {
      if (isCoachInitiated) {
        newError["selectedLearner"] = !data.selectedLearner
          ? "Please select a learner"
          : null;
        newError["selectedCompetencySkill"] = !data.selectedCompetencySkill
          ? "Please select a competency skill"
          : null;
        newError["selectedCoach"] = null;

        isValid = ![
          newError.selectedCompetencySkill,
          newError.selectedLearner,
        ].some((val) => val !== null);
      } else {
        newError["selectedCompetencySkill"] = !data.selectedCompetencySkill
          ? "Please select a competency skill"
          : null;
        newError["selectedCoach"] = !data.selectedCoach
          ? "Please select a coach"
          : null;

        isValid = ![
          newError.selectedCompetencySkill,
          newError.selectedCoach,
        ].some((val) => val !== null);
      }
    }

    if (step === STEPS.REFLECT) {
      if (isCoachInitiated) {
        return true;
      }
      const isITER =
        data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;

      if (isITER && data.selectedCompetencySkill?.competencies) {
        const subRequirements = data.selectedCompetencySkill.competencies
          .map((c: any) => c.competency)
          .filter(Boolean);

        // Initialize individual error tracking for ratings
        const iterRateErrors: Record<string, string | null> = {};
        // Initialize individual error tracking for comments
        const iterCommentErrors: Record<string, string | null> = {};
        
        // Check each sub-requirement individually
        let allRated = true;
        let allCommented = true;
        subRequirements.forEach((subReq: any) => {
          const isRated =
            data.iterSubRequirementRates[subReq.requirement_id] !== null &&
            data.iterSubRequirementRates[subReq.requirement_id] !== undefined;
          
          if (!isRated) {
            allRated = false;
            iterRateErrors[subReq.requirement_id] =
              `Please rate ${subReq.name || "this competency"}`;
          } else {
            iterRateErrors[subReq.requirement_id] = null;
          }

          const comment = data.iterSubRequirementComments[subReq.requirement_id] || "";
          if (!comment.trim()) {
            allCommented = false;
            iterCommentErrors[subReq.requirement_id] =
              `Please enter a comment for ${subReq.name || "this competency"}`;
          } else {
            iterCommentErrors[subReq.requirement_id] = null;
          }
        });
        newError["iterSubRequirementRateErrors"] = iterRateErrors;
        newError["iterSubRequirementCommentErrors"] = iterCommentErrors;

        newError["rate"] = !allRated
          ? "Please rate all sub-requirements"
          : null;
        newError["perform"] = !allCommented
          ? "Please enter comments for all competencies"
          : null;
        isValid = !newError.perform && !newError.rate;
      } else {
        newError["perform"] = !data.perform ? "Please enter this field" : null;
        newError["rate"] = !data.rate
          ? "Please click the rate you think you performed at"
          : null;
        isValid = !newError.perform && !newError.rate;
      }

      newError["coachAvailable"] =
        data.coachAvailable == null
          ? "Please say if your coach is with you"
          : null;
      isValid = isValid && !newError.coachAvailable;
    }

    if (step === STEPS.REFLECT_COACH) {
      newError["coachObserve"] = !data.coachObserve
        ? "Please enter this field"
        : null;

      const isITER =
        data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;

      if (isITER && data.selectedCompetencySkill?.competencies) {
        const subRequirements = data.selectedCompetencySkill.competencies
          .map((c: any) => c.competency)
          .filter(Boolean);

        // Initialize individual error tracking
        const iterCoachRateErrors: Record<string, string | null> = {};
        
        // Check each sub-requirement individually
        let allRated = true;
        subRequirements.forEach((subReq: any) => {
          const isRated =
            data.iterSubRequirementCoachRates[subReq.requirement_id] !== null &&
            data.iterSubRequirementCoachRates[subReq.requirement_id] !==
              undefined;
          
          if (!isRated) {
            allRated = false;
            iterCoachRateErrors[subReq.requirement_id] =
              `Please rate ${subReq.name || "this competency"}`;
          } else {
            iterCoachRateErrors[subReq.requirement_id] = null;
          }
        });
        newError["iterSubRequirementCoachRateErrors"] = iterCoachRateErrors;

        newError["coachRate"] = !allRated
          ? "Please rate all sub-requirements"
          : null;
        isValid = !newError.coachObserve && !newError.coachRate;
      } else {
        newError["coachRate"] = !data.coachRate
          ? "Please rate the performance"
          : null;
        isValid = !newError.coachObserve && !newError.coachRate;
      }
    }
    setError(newError);
    return isValid;
  };

  const handleSaveFocusFeedback = async () => {
    const objects = data.selectedFocusFeedback.map((tag: Tag) => ({
      feedback_id: feedbackIdRef.current,
      tag_id: tag.tag_id,
    }));
    await addFeedbackTags({
      variables: {
        objects,
      },
    });
  };

  const dispatchNotificationSuccessDraft = () => {
    dispatchRedux(
      showNotificationThunk({
        message: "Changes saved",
        severity: "success",
      }),
    );
  };

  const getVariables = () => {
    // Save the draft for the request step

    let variables: any = {
      org_id: orgId,
      student_id: selectedUser?.user_id,
      coach_id: data.selectedCoach?.user_id,
      status: feedback?.status || data.status,
    };

    const isITER =
      data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;

    // Get the observation date
    const observationDate =
      data.caseDate && data.caseTime
        ? new Date(
            `${data.caseDate.format("YYYY-MM-DD")} ${data.caseTime.format("HH:mm:ss")}`,
          )
        : new Date(); // Fallback to current date/time

    // For ITER, always set observation_date since we skip REQUEST steps
    if (isITER) {
      variables["observation_date"] = observationDate.toISOString();
    } else if (step === STEPS.REQUEST) {
      // First view for request step (non-ITER)
      variables["observation_date"] = observationDate.toISOString();
      variables["animal_name"] = data.name;
      if (data.mrn) variables["mrn"] = data.mrn;
    } else if (step === STEPS.REQUEST1) {
      // Second view for request step

      variables["species_id"] = data.selectedSpecies?.species_id;
      variables["animal_sex"] = data.animalSex;
      variables["course"] = data.course;
      variables["site_id"] = data.selectedSite?.site_id;
    } else if (step === STEPS.REQUEST2) {
      variables["coach_id"] = data.selectedCoach?.user_id;
    }

    return variables;
  };

  const getVariablesSkill = () => {
    let variablesSkill: any = {};
    variablesSkill["coach_comments"] = data.coachObserve;
    // Coach-initiated feedback has no learner self score.
    variablesSkill["self_rating"] = isCoachInitiated ? null : data.rate;
    variablesSkill["coach_rating"] = data.coachRate;
    variablesSkill["student_comments"] = isCoachInitiated ? "" : data.perform;
    variablesSkill["areas"] = isCoachInitiated ? "" : data.suggestion;
    variablesSkill["actions"] = isCoachInitiated ? "" : data.improvement;
    variablesSkill["requirement_id"] =
      data.selectedCompetencySkill?.requirement_id;
    variablesSkill["feedback_id"] = feedbackIdRef.current;
    return variablesSkill;
  };

  const getITERSubRequirementSkills = (useCoachRate: boolean = false) => {
    if (
      data.selectedCompetencySkill?.type !== REQUIREMENT.REQUIREMENT_ITER ||
      !data.selectedCompetencySkill?.competencies
    ) {
      return [];
    }

    const subRequirements = data.selectedCompetencySkill.competencies
      .map((c: any) => c.competency)
      .filter(Boolean);

    return subRequirements.map((subReq: any) => ({
      coach_comments: data.coachObserve,
      self_rating: isCoachInitiated
        ? null
        : data.iterSubRequirementRates[subReq.requirement_id] || null,
      coach_rating: useCoachRate
        ? data.iterSubRequirementCoachRates[subReq.requirement_id] || null
        : null,
      student_comments: isCoachInitiated
        ? ""
        : data.iterSubRequirementComments[subReq.requirement_id] || "",
      areas: isCoachInitiated ? "" : data.suggestion,
      actions: isCoachInitiated ? "" : data.improvement,
      requirement_id: subReq.requirement_id,
      feedback_id: feedbackIdRef.current,
    }));
  };

  const onSaveDraft = async () => {
    // Prevent concurrent saves
    if (isSavingRef.current) {
      console.log("Save already in progress, skipping...");
      return;
    }

    try {
      isSavingRef.current = true;

      // Ensure feedbackIdRef is initialized from URL param if available
      if (feedbackId && !feedbackIdRef.current) {
        feedbackIdRef.current = feedbackId;
      }

      // create variables for feedback
      let variables: any = getVariables();
      // create variables for feedback skill
      let variablesSkill: any = getVariablesSkill();

      // Double-check feedbackIdRef before creating (in case it was set by another concurrent call)
      if (!feedbackIdRef.current) {
        // if no feedback id, add the feedback and set the feedback id
        const response = await addFeedback({ variables });

        console.log("Add Feedback Response:", response);

        // Double-check again after creation (in case another call created it while we were waiting)
        if (!feedbackIdRef.current && response.data?.insert_feedback?.returning[0]?.feedback_id) {
          if (response.data?.insert_feedback?.returning[0].created_at) {
            setTimerStart(response.data?.insert_feedback?.returning[0].created_at);
          }
          feedbackIdRef.current =
            response.data?.insert_feedback?.returning[0].feedback_id;

          variablesSkill["feedback_id"] = feedbackIdRef.current;

          dispatchNotificationSuccessDraft();
        } else if (feedbackIdRef.current) {
          // Another call created the feedback, just update it
          console.log("Feedback was created by another call, updating instead...");
          await updateFeedback({
            variables: {
              id: feedbackIdRef.current,
              feedback: variables,
            },
          });
          dispatchNotificationSuccessDraft();
        }
      } else {
      console.log(
        "Updating Feedback with variables:",
        feedbackIdRef.current,
        variables,
      );
      // Update existing feedback
      await updateFeedback({
        variables: {
          id: feedbackIdRef.current,
          feedback: variables,
        },
      });
      dispatchNotificationSuccessDraft();
    }

    const isITER =
      data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;

    if (isITER) {
      // For ITER, always refetch the feedback to get the latest feedback_skills
      // This ensures we don't create duplicates even if they were created in previous calls
      let latestFeedbackSkills: any[] = [];
      if (feedbackIdRef.current) {
        const feedbackResponse = await getFeedbackById({
          variables: { feedbackId: feedbackIdRef.current },
        });
        if (feedbackResponse.data?.feedback_by_pk?.feedback_skills) {
          latestFeedbackSkills =
            feedbackResponse.data.feedback_by_pk.feedback_skills;
          // Clear and repopulate the ref with all existing feedback_skills to ensure it's up-to-date
          iterFeedbackSkillIdsRef.current = {};
          latestFeedbackSkills.forEach((skill: any) => {
            if (skill.requirement?.requirement_id && skill.feedback_skill_id) {
              iterFeedbackSkillIdsRef.current[
                skill.requirement.requirement_id
              ] = skill.feedback_skill_id;
            }
          });
        } else {
          // If no feedback_skills exist, clear the ref
          iterFeedbackSkillIdsRef.current = {};
        }
      }

      // For ITER, create or update feedback_skills for each sub-requirement
      // Use coach rates if we're past the ReflectCoach step, otherwise use student rates
      const useCoachRates =
        step === STEPS.REFLECT_COACH || step === STEPS.SUBMIT;
      const iterSkills = getITERSubRequirementSkills(useCoachRates);
      for (const skill of iterSkills) {
        if (skill.requirement_id) {
          // Check the ref first (which we just populated), then fall back to latestFeedbackSkills
          const existingFeedbackSkillId =
            iterFeedbackSkillIdsRef.current[skill.requirement_id] ||
            latestFeedbackSkills.find(
              (fs) => fs.requirement?.requirement_id === skill.requirement_id,
            )?.feedback_skill_id;

          if (existingFeedbackSkillId) {
            // Update existing feedback_skill
            await updateFeedbackSkill({
              variables: {
                id: existingFeedbackSkillId,
                feedback_skill: skill,
              },
            });
            // Ensure the ref is updated
            iterFeedbackSkillIdsRef.current[skill.requirement_id] =
              existingFeedbackSkillId;
          } else {
            // Create new feedback_skill only if it doesn't exist
            const responseSkill = await addFeedbackSkill({
              variables: skill,
            });
            const newFeedbackSkillId =
              responseSkill.data?.insert_feedback_skill?.returning[0]
                ?.feedback_skill_id;
            if (newFeedbackSkillId) {
              iterFeedbackSkillIdsRef.current[skill.requirement_id] =
                newFeedbackSkillId;
            }
          }
        }
      }
    } else {
      // For non-ITER requirements, use the existing logic
      if (!feedbackSkillIdRef.current && variablesSkill["requirement_id"]) {
        const responseSkill = await addFeedbackSkill({
          variables: variablesSkill,
        });
        feedbackSkillIdRef.current =
          responseSkill.data?.insert_feedback_skill?.returning[0].feedback_skill_id;
      } else if (feedbackSkillIdRef.current) {
        updateFeedbackSkill({
          variables: {
            id: feedbackSkillIdRef.current,
            feedback_skill: variablesSkill,
          },
        });
        dispatchNotificationSuccessDraft();
      }
    }
    } finally {
      isSavingRef.current = false;
    }
  };

  /* ************************** ON SUBMIT ************************** */
  const onSubmit = async () => {
    // Ensure feedbackIdRef is initialized from URL param if available
    if (feedbackId && !feedbackIdRef.current) {
      feedbackIdRef.current = feedbackId;
    }

    const isITER =
      data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;

    if (isITER) {
      // For ITER, always refetch the feedback to get the latest feedback_skills before submitting
      let latestFeedbackSkills: any[] = [];
      if (feedbackIdRef.current) {
        const feedbackResponse = await getFeedbackById({
          variables: { feedbackId: feedbackIdRef.current },
        });
        if (feedbackResponse.data?.feedback_by_pk?.feedback_skills) {
          latestFeedbackSkills =
            feedbackResponse.data.feedback_by_pk.feedback_skills;
          // Clear and repopulate the ref with all existing feedback_skills to ensure it's up-to-date
          iterFeedbackSkillIdsRef.current = {};
          latestFeedbackSkills.forEach((skill: any) => {
            if (skill.requirement?.requirement_id && skill.feedback_skill_id) {
              iterFeedbackSkillIdsRef.current[
                skill.requirement.requirement_id
              ] = skill.feedback_skill_id;
            }
          });
        } else {
          // If no feedback_skills exist, clear the ref
          iterFeedbackSkillIdsRef.current = {};
        }
      }

      // For ITER, update feedback_skills for each sub-requirement with coach ratings
      const iterSkills = getITERSubRequirementSkills(true); // Pass true to include coach rates
      for (const skill of iterSkills) {
        if (skill.requirement_id) {
          // Check the ref first (which we just populated), then fall back to latestFeedbackSkills
          const existingFeedbackSkillId =
            iterFeedbackSkillIdsRef.current[skill.requirement_id] ||
            latestFeedbackSkills.find(
              (fs) => fs.requirement?.requirement_id === skill.requirement_id,
            )?.feedback_skill_id;

          if (existingFeedbackSkillId) {
            // Update existing feedback_skill
            await updateFeedbackSkill({
              variables: {
                id: existingFeedbackSkillId,
                feedback_skill: skill,
              },
            });
            // Ensure the ref is updated
            iterFeedbackSkillIdsRef.current[skill.requirement_id] =
              existingFeedbackSkillId;
          } else {
            // Create new feedback_skill (shouldn't happen at submit, but handle it)
            const responseSkill = await addFeedbackSkill({
              variables: skill,
            });
            const newFeedbackSkillId =
              responseSkill.data?.insert_feedback_skill?.returning[0]
                ?.feedback_skill_id;
            if (newFeedbackSkillId) {
              iterFeedbackSkillIdsRef.current[skill.requirement_id] =
                newFeedbackSkillId;
            }
          }
        }
      }
    } else {
      // For non-ITER, update the single feedback_skill with coach rating
      if (feedbackSkillIdRef.current) {
        await updateFeedbackSkill({
          variables: {
            id: feedbackSkillIdRef.current,
            feedback_skill: {
              coach_rating: data.coachRate,
              coach_comments: data.coachObserve,
              self_rating: isCoachInitiated ? null : data.rate,
            },
          },
        });
      }
    }

    if (isCoachInitiated) {
      const response = await updateFeedback({
        refetchQueries: [
          "getHomeDataStudent",
          "getHomeDataCoach",
          "getHomeLearnerActivity",
          "getHomeCoachActivity",
        ],
        variables: {
          id: feedbackIdRef.current,
          feedback: {
            status: FeedbackStatus.DONE,
            reviewed_at: new Date().toISOString(),
            feedback_type: data.coachAvailable ? "IN-PERSON" : "ONLINE",
            coach_id: user?.user_id || data.selectedCoach?.user_id,
            student_id: selectedUser?.user_id,
          },
        },
      });

      if (response.data?.update_feedback?.affected_rows === 1) {
        try {
          await awardVetcoins({
            userId: user?.user_id || "",
            amount: VETCOIN_REWARDS.FEEDBACK_EVALUATION_COMPLETED,
            actionType: "FEEDBACK_EVALUATION_COMPLETED",
            sourceFeedbackId: feedbackIdRef.current,
            metadata: { flow: "coach_give_feedback_submit" },
          });
        } catch (error) {
          console.error("Error awarding vetcoins for coach give feedback:", error);
        }

        try {
          await checkAndAwardBadge();
        } catch (error) {
          console.error("Error checking badge eligibility:", error);
        }

        dispatchRedux(
          showNotificationThunk({
            message: "Feedback Submitted",
            severity: "success",
          }),
        );
        dispatchRedux(
          sendAlertViaEmail({
            feedbackId: feedbackIdRef.current || "",
            coachName: user?.name || data.selectedCoach?.name || "",
            studentName: selectedUser?.name || "",
            studentId: selectedUser?.user_id || "",
            studentEmail: selectedUser?.email || "",
            type: ALERT_TYPES.COACH_GAVE_FEEDBACK,
          }),
        );
        setIsCompleted(true);
      }
      if (feedbackIdRef.current && data.selectedFocusFeedback.length > 0) {
        handleSaveFocusFeedback();
      }
      return;
    }

    const response = await updateFeedback({
        refetchQueries: [
          "getHomeDataStudent",
          "getHomeDataCoach",
          "getHomeLearnerActivity",
          "getHomeCoachActivity",
        ],
        variables: {
          id: feedbackIdRef.current,
          feedback: {
            status: FeedbackStatus.PENDING,
          feedback_type: data.coachAvailable ? "IN-PERSON" : "ONLINE",
        },
      },
    });

    if (response.data?.update_feedback?.affected_rows === 1) {
      try {
        await awardVetcoins({
          userId: selectedUser?.user_id || "",
          amount: VETCOIN_REWARDS.FEEDBACK_REQUEST_SUBMITTED,
          actionType: "FEEDBACK_REQUEST_SUBMITTED",
          sourceFeedbackId: feedbackIdRef.current,
          metadata: { flow: "learner_request_feedback_submit" },
        });
      } catch (error) {
        console.error("Error awarding vetcoins for feedback submit:", error);
      }

      // Check for badge eligibility after submitting feedback request (for learners)
      try {
        await checkAndAwardBadge();
      } catch (error) {
        console.error("Error checking badge eligibility:", error);
        // Don't block feedback submission if badge check fails
      }

      dispatchRedux(
        showNotificationThunk({
          message: "Feedback Request Submitted",
          severity: "success",
        }),
      );
      dispatchRedux(
        sendAlertViaEmail({
          feedbackId: feedbackIdRef.current || "",
          coachName: data.selectedCoach?.name || "",
          coachEmail: data.selectedCoach?.email || "",
          coachId: data.selectedCoach?.user_id || "",
          studentName: selectedUser?.name || "",
          type: ALERT_TYPES.FEEDBACK_REQUEST,
        }),
      );
      setIsCompleted(true);
    }
    if (feedbackIdRef.current && data.selectedFocusFeedback.length > 0) {
      handleSaveFocusFeedback();
    }
  };

  const shouldSkipReviewEpaSummaryStep = () => {
    if (typeof window === "undefined") return false;
    return (
      window.localStorage.getItem(SKIP_REVIEW_EPA_SUMMARY_STORAGE_KEY) === "true"
    );
  };

  const handleStep = async (newStep: string, action: string = "next") => {
    // check if the data is valid
    const isValidData = isValid();
    if (!isValidData && action === "next") {
      // if the data is not valid, show the error message
      return;
    }

    const isITER =
      data.selectedCompetencySkill?.type === REQUIREMENT.REQUIREMENT_ITER;
    const skipReviewEpaSummary = !isITER && shouldSkipReviewEpaSummaryStep();

    if (
      (newStep === STEPS.REQUEST || newStep === STEPS.REQUEST1) &&
      action === "next"
    ) {
      await onSaveDraft();
    } else if (
      newStep === STEPS.REQUEST1 &&
      action === "next" &&
      status === FeedbackStatus.MISSING_INFO
    ) {
      navigate(CoachRoutes.FeedbackPortal);
    } else if (newStep === STEPS.PERFORM && action === "next") {
      await onSaveDraft();
      if (isCoachInitiated) {
        if (skipReviewEpaSummary) {
          setActiveLabel(2);
          setStep(STEPS.REFLECT_COACH);
          setDisplayTimer(true);
          return;
        }
        setActiveLabel(1);
      } else if (skipReviewEpaSummary) {
        setActiveLabel(2);
        setStep(STEPS.REFLECT);
        setDisplayTimer(true);
        return;
      } else {
        setActiveLabel(1);
      }
    } else if (
      step === STEPS.REFLECT ||
      (step === STEPS.REFLECT_COACH && action === "next")
    ) {
      await onSaveDraft();
    } else if (newStep === STEPS.REFLECT && action === "next") {
      setActiveLabel(2);
    } else if (newStep === STEPS.REFLECT_COACH && action === "next") {
      setActiveLabel(isCoachInitiated ? 2 : 3);
    } else if (newStep === STEPS.SUBMIT && action === "next") {
      setActiveLabel(isCoachInitiated ? 3 : 4);
    } else if (newStep === STEPS.REQUEST2 && action === "back") {
      setActiveLabel(0);
    } else if (newStep === STEPS.REQUEST && action === "back") {
      setActiveLabel(0);
    } else if (newStep === STEPS.PERFORM && action === "back") {
      if (skipReviewEpaSummary) {
        setActiveLabel(0);
        setStep(STEPS.REQUEST1);
        return;
      }
      setActiveLabel(1);
    } else if (
      (newStep === STEPS.REFLECT_COACH || newStep === STEPS.REFLECT) &&
      action === "back"
    ) {
      setActiveLabel(isCoachInitiated ? 1 : 2);
    }

    setStep(newStep);
  };

  const onBegin = () => {
    handleStep(isCoachInitiated ? STEPS.REFLECT_COACH : STEPS.REFLECT);
    setDisplayTimer(true);
  };

  /**
   * Jump to a high-level stepper phase the user has already reached.
   * Skips field validation so completed steps remain revisitable.
   */
  const goToStepperLabel = (labelIndex: number) => {
    if (labelIndex < 0 || labelIndex > activeLabel) {
      return;
    }

    const targetSteps = isCoachInitiated
      ? [STEPS.REQUEST2, STEPS.PERFORM, STEPS.REFLECT_COACH, STEPS.SUBMIT]
      : [
          STEPS.REQUEST2,
          STEPS.PERFORM,
          STEPS.REFLECT,
          STEPS.REFLECT_COACH,
          STEPS.SUBMIT,
        ];
    const targetStep = targetSteps[labelIndex];
    if (!targetStep) {
      return;
    }

    // Request covers several sub-steps; always land on the first one.
    if (labelIndex === 0) {
      if (step === STEPS.REQUEST2) {
        return;
      }
      setActiveLabel(0);
      setStep(STEPS.REQUEST2);
      return;
    }

    if (targetStep === step) {
      return;
    }

    setActiveLabel(labelIndex);
    setStep(targetStep);
  };

  const coaches = useMemo(() => {
    const fromOrg =
      coachesByOrgData?.organization_user
        ?.map((row) => row.user)
        ?.filter((user): user is NonNullable<typeof user> => !!user?.user_id) ||
      [];

    if (fromOrg.length > 0) {
      return fromOrg as User[];
    }

    // Fallback: mentors assigned to the learner
    const fromMentors =
      (dataFeedback as any)?.mentors
        ?.map((row: any) => row.mentor)
        ?.filter((mentor: any) => !!mentor?.user_id) || [];

    return fromMentors as User[];
  }, [coachesByOrgData, dataFeedback]);

  const learners = useMemo((): User[] => {
    const byId = new Map<string, User>();

    for (const gc of learnersByOrgData?.graduation_class ?? []) {
      for (const row of gc.students ?? []) {
        const student = row.student;
        if (!student?.user_id) continue;
        byId.set(student.user_id, student as User);
      }
    }

    for (const membership of orgStudentsData?.organization_user ?? []) {
      const student = membership.user;
      if (!student?.user_id) continue;
      byId.set(student.user_id, {
        ...(byId.get(student.user_id) || {}),
        ...student,
      } as User);
    }

    return [...byId.values()].sort((a, b) =>
      (a.name || a.email).localeCompare(b.name || b.email),
    );
  }, [learnersByOrgData, orgStudentsData]);

  useEffect(() => {
    if (!orgId) return;
    if (isCoachInitiated) {
      getLearnersByOrgId({
        variables: { orgId },
        fetchPolicy: "network-only",
      }).then();
      getOrgStudents({
        variables: { orgId, role: ROLES.STUDENT },
        fetchPolicy: "network-only",
      }).then();
      return;
    }
    getCoachesByOrg({
      variables: { orgId, role: ROLES.COACH },
      fetchPolicy: "network-only",
    }).then();
  }, [
    orgId,
    isCoachInitiated,
    getCoachesByOrg,
    getLearnersByOrgId,
    getOrgStudents,
  ]);

  const historicalFeedbacks = useMemo(() => {
    return (historicalFeedbacksData?.feedback as FeedbackType[]) || [];
  }, [historicalFeedbacksData]);

  return {
    data,
    status,
    step,
    dataFeedback,
    error,
    isCompleted,
    sites,
    activeLabel,
    tags,
    timerStart,
    displayTimer,
    requirements,
    dispatch,
    customLabels,
    onSubmit,
    handleStep,
    goToStepperLabel,
    onBegin,
    coaches,
    learners,
    isCoachInitiated,
    historicalFeedbacks,
  };
};
